import {
  Announcements,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  ScreenReaderInstructions,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { GripVertical } from 'lucide-react';
import React, { useMemo } from 'react';
import UDIconButton from 'ud-ui/components/icon-button';

export interface UDSortableItemState {
  /** Place in the list, from zero. */
  index: number;
  /** The grip to drag the item by — put it where the row starts (`UDEntityRow` `leading`). */
  dragHandle: React.ReactNode;
  isDragging: boolean;
}

export interface UDSortableListProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  items: readonly T[];
  /** Stable and unique: dragging follows the id, not the index. */
  getId: (item: T) => string;
  /** What the item is called for a person: the grip's name and the screen reader's announcements. */
  itemLabel: (item: T) => string;
  /** The item `id` was dropped at place `toIndex`; the others shift (`arrayMove`). The screen keeps the order. */
  onMove: (id: string, toIndex: number) => void;
  renderItem: (item: T, state: UDSortableItemState) => React.ReactNode;
  disabled?: boolean;
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const prefersReducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.(REDUCED_MOTION_QUERY).matches;

/**
 * A vertical list reordered by dragging a grip — the one way to drag rows in the project (dnd-kit).
 *
 * The list knows nothing of the rows: it hands `renderItem` a ready grip and moves nothing itself — `onMove` reports
 * where an item was dropped and the screen changes its array. Dragging is by the grip only, so the row's buttons and
 * selects stay clickable. Keyboard: Tab to the grip, Space — take, ↑↓ — move, Space — drop, Esc — cancel.
 *
 * Dragging alone is not enough (WCAG 2.5.7): give each row a way without dragging too — ↑↓ buttons or a place
 * number — calling the same `onMove`.
 */
function UDSortableList<T>(props: UDSortableListProps<T>) {
  const { items, getId, itemLabel, onMove, renderItem, disabled, className, ...rest } = props;
  const { t } = useLingui();

  const sensors = useSensors(
    // Five pixels before a drag starts: a plain click on the grip stays a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => items.map(getId), [items, getId]);

  const labelOf = (id: UniqueIdentifier) => {
    const item = items[ids.indexOf(String(id))];
    return item === undefined ? String(id) : itemLabel(item);
  };
  const placeOf = (id: UniqueIdentifier) => ids.indexOf(String(id)) + 1;
  const total = items.length;

  const screenReaderInstructions: ScreenReaderInstructions = {
    draggable: t`Чтобы перетащить, нажмите Пробел. Стрелками вверх и вниз — переместить, Пробел — поставить, Escape — отменить.`,
  };
  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      const label = labelOf(active.id);
      const place = placeOf(active.id);
      return t`Взят элемент «${label}», место ${place} из ${total}.`;
    },
    onDragOver: ({ active, over }) => {
      if (!over) return undefined;
      const label = labelOf(active.id);
      const place = placeOf(over.id);
      return t`«${label}» над местом ${place} из ${total}.`;
    },
    onDragEnd: ({ active, over }) => {
      const label = labelOf(active.id);
      if (!over) return t`«${label}» оставлен на месте.`;
      const place = placeOf(over.id);
      return t`«${label}» поставлен на место ${place} из ${total}.`;
    },
    onDragCancel: ({ active }) => {
      const label = labelOf(active.id);
      return t`Перетаскивание отменено, «${label}» на прежнем месте.`;
    },
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const toIndex = ids.indexOf(String(over.id));
    if (toIndex >= 0) onMove(String(active.id), toIndex);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      accessibility={{ announcements, screenReaderInstructions }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy} disabled={disabled}>
        <div {...rest} className={classNames('flex flex-col', className)}>
          {items.map((item, index) => (
            <SortableItem key={ids[index]} id={ids[index]} label={itemLabel(item)} disabled={disabled}>
              {(dragHandle, isDragging) => renderItem(item, { index, dragHandle, isDragging })}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortableItemProps {
  id: string;
  label: string;
  disabled?: boolean;
  children: (dragHandle: React.ReactNode, isDragging: boolean) => React.ReactNode;
}

function SortableItem({ id, label, disabled, children }: SortableItemProps) {
  const { t } = useLingui();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
    transition: prefersReducedMotion() ? null : { duration: 200, easing: 'ease-out' },
  });

  const dragHandle = (
    <UDIconButton
      ref={setActivatorNodeRef}
      icon={<GripVertical />}
      variant="ghost"
      size="sm"
      aria-label={t`Перетащить: ${label}`}
      tooltip={isDragging ? undefined : t`Потяните, чтобы переставить`}
      disabled={disabled}
      className={classNames('touch-none', isDragging ? 'cursor-grabbing' : 'cursor-grab')}
      {...attributes}
      {...listeners}
    />
  );

  return (
    <div
      ref={setNodeRef}
      // The dragged row is lifted above its neighbours — a shadow, not translucency.
      className={classNames('rounded-card', { 'shadow-medium z-content relative': isDragging })}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      data-dragging={isDragging || undefined}
    >
      {children(dragHandle, isDragging)}
    </div>
  );
}

export default UDSortableList;
